import { useState, useEffect } from "react";
import { ExternalLink, Download, ClipboardList, GitCommit, Clock } from "lucide-react";
import { Modal, StatTile, Button } from "../../../shared/ui";
import StatusBadge from "./StatusBadge.jsx";
import { formatDateTime } from "../../utils/dateUtils.js";
import { getBlobUrlFromFileUrl, isExternalDocumentUrl } from "../../utils/downloadUtil.js";
import DynamicFileViewer from "../../../components/FileViewers/DynamicFileViewer";

const SUMMARY_MAX = 280;

const JobDetailsModal = ({ job, onClose, downloadingId, onDownload }) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [isViewing, setIsViewing] = useState(false);

  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerFilename, setViewerFilename] = useState("");

  useEffect(() => {
    setSummaryExpanded(false);
  }, [job]);

  if (!job) return null;

  const handleViewClick = async () => {
    if (!job.published_url) return;
    if (isExternalDocumentUrl(job.published_url)) {
      window.open(job.published_url, "_blank", "noopener,noreferrer");
      return;
    }
    setIsViewing(true);
    try {
      const { blobUrl, filename } = await getBlobUrlFromFileUrl(job.published_url);
      setViewerBlobUrl(blobUrl);
      setViewerFilename(filename);
      setViewerModalOpen(true);
    } catch (err) {
      console.error("Failed to open document:", err);
    } finally {
      setIsViewing(false);
    }
  };

  const closeViewerModal = () => {
    setViewerModalOpen(false);
    if (viewerBlobUrl) {
      window.URL.revokeObjectURL(viewerBlobUrl);
      setViewerBlobUrl(null);
    }
  };

  const subtitle = job.repository || job.source_version || job.release_tag ? (
    <p className="text-sm text-muted mt-1 flex flex-wrap items-center gap-1.5">
      {job.repository && <span className="truncate max-w-xs">{job.repository}</span>}
      {job.source_version && (
        <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap">
          {job.source_version} → {job.target_version}
        </span>
      )}
      {job.release_tag && (
        <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap">
          {job.release_tag}
        </span>
      )}
    </p>
  ) : null;

  return (
    <Modal
      open={Boolean(job)}
      title={job.document_title || "Generation Details"}
      onClose={onClose}
      size="lg"
    >
      <div className="flex flex-col gap-5">
        {subtitle}

        {job.status === "completed" ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatTile icon={ClipboardList} label="Work Items" value={job.total_work_items ?? 0} />
              <StatTile icon={GitCommit} label="Diffs" value={job.total_diffs ?? 0} />
              <StatTile icon={Clock} label="Generated" value={formatDateTime(job.updated_at) || "—"} />
            </div>

            {job.executive_summary && (
              <div className="bg-gray-50 rounded-card border border-line p-4">
                <p className="text-[11px] uppercase font-bold text-muted mb-3">Executive Summary</p>
                <p className="text-sm text-ink leading-relaxed m-0">
                  {summaryExpanded || job.executive_summary.length <= SUMMARY_MAX
                    ? job.executive_summary
                    : job.executive_summary.slice(0, SUMMARY_MAX).trimEnd() + "…"}
                </p>
                {job.executive_summary.length > SUMMARY_MAX && (
                  <button
                    type="button"
                    onClick={() => setSummaryExpanded((v) => !v)}
                    className="mt-2 text-xs font-semibold text-brand-700 hover:text-brand-800 border-0 bg-transparent cursor-pointer p-0"
                  >
                    {summaryExpanded ? "Show less" : "Read more"}
                  </button>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleViewClick}
                disabled={isViewing || !job.published_url}
                loading={isViewing}
              >
                <ExternalLink size={14} />
                {isViewing ? "Opening…" : "View Document"}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onDownload(job)}
                disabled={!!downloadingId || !job.published_url}
                loading={downloadingId === job.job_id}
              >
                <Download size={14} />
                {downloadingId === job.job_id ? "Downloading…" : "Download File"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-10">
            <StatusBadge status={job.status} />
            {job.status === "failed" && job.error_message && (
              <p className="text-sm text-status-critical text-center max-w-md">{job.error_message}</p>
            )}
            {(job.status === "queued" || job.status === "running") && (
              <p className="text-sm text-muted">Document generation is still in progress.</p>
            )}
          </div>
        )}
      </div>
      <Modal
        open={viewerModalOpen}
        onClose={closeViewerModal}
        title={viewerFilename || "Document Preview"}
        size="lg"
      >
        <DynamicFileViewer
          fileUrl={viewerBlobUrl}
          filename={viewerFilename || job.published_url}
        />
      </Modal>
    </Modal>
  );
};

export default JobDetailsModal;
